'use client';

/**
 * 全站只挂一处：PayPal / Stripe Hosted Checkout 整页跳转回跳后，统一从 URL 上的
 * option/order_id 等参数完成 commerce confirm。与既有 FirebaseLoginRedirectHandler 对齐。
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import dynamic from 'next/dynamic';

import { CommerceApi } from '@/apis/commerce';
import LoadingAnimation from '@/components/loading-animation';
import { assertConfirmOrderResponse, confirmCommerceOrderWithPolling } from '@/hooks/use-pay/confirm-with-polling';
import { clearPersistedPayPendingMeta, readPersistedPayPendingMeta } from '@/hooks/use-pay/paypal-checkout-redirect';
import {
    EHostedCheckoutReturnQueryKey,
    parseHostedCheckoutReturnFromSearchParams,
    stripHostedCheckoutReturnFromUrl,
} from '@/lib/pay/hosted-checkout-return-params';
import {
    clearPayPendingReturnUrls,
    isBackForwardNavigation,
    mergeLandingPersistAfterCheckoutReturn,
    readPayPendingReturnUrls,
    tryRestoreStrippedPayReturnUrl,
} from '@/lib/pay/hosted-checkout-return-restore';
import { useToast } from '@/providers/root/toast';
import { useAssetStore } from '@/store/asset';
import { readArmsFailDetail } from '@/utils/arms/paypal-events';
import {
    queuePayResultTrackingAfterRefresh,
    trackRechargePageResultPayCancel,
    trackRechargePageResultPayFail,
} from '@/utils/tracker/pay';

import {
    formatConfirmOrderFailDetail,
    mapConfirmStatusToCheckoutFailReason,
    metaToPayExtras,
    metaToStaticContext,
    reportCheckoutReturnFail,
    reportCheckoutReturnSuccess,
} from './checkout-return-arms';

const PayCancelReasonModal = dynamic(() => import('@/components/paypal-cancel-reason-modal'), {
    ssr: false,
    loading: () => null,
});

let hasProcessedReturn = false;

const CONSUMED_KEY = (orderId: string): string => `hosted_checkout_consumed_${orderId}`;

/** 兼容旧版仅写入 paypal_consumed_* 的 session */
const LEGACY_CONSUMED_KEY = (orderId: string): string => `paypal_consumed_${orderId}`;

const DEFAULT_STORE_PAYPAL = 'PAYPAL';
const DEFAULT_STORE_STRIPE_CHECKOUT = 'STRIPE_CHECKOUT';

function getReturnSearchParams(): URLSearchParams {
    if (typeof window === 'undefined') {
        return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
}

function resolveConfirmStore(meta: ReturnType<typeof readPersistedPayPendingMeta>): string {
    if (meta?.store) {
        return meta.store;
    }
    if (['card', 'apple', 'google'].includes(meta?.payment_method ?? '')) {
        return DEFAULT_STORE_STRIPE_CHECKOUT;
    }
    return DEFAULT_STORE_PAYPAL;
}

function isOrderReturnConsumed(orderId: string): boolean {
    try {
        return (
            window.sessionStorage.getItem(CONSUMED_KEY(orderId)) === '1' ||
            window.sessionStorage.getItem(LEGACY_CONSUMED_KEY(orderId)) === '1'
        );
    } catch {
        return false;
    }
}

function markOrderReturnConsumed(orderId: string): void {
    try {
        window.sessionStorage.setItem(CONSUMED_KEY(orderId), '1');
    } catch {
        // sessionStorage 不可用时跳过防重保护
    }
}

export default function HostedCheckoutReturnConfirmHandler() {
    const t = useTranslations('Paywall');

    const { addToast } = useToast();
    const [isConfirming, setIsConfirming] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [orderId, setOrderId] = useState('');
    const handleCancelModalClose = useCallback(() => setShowCancelModal(false), []);
    const paymentCancelSurveyOrderId = useAssetStore(state => state.paymentCancelSurveyOrderId);

    useEffect(() => {
        if (!paymentCancelSurveyOrderId) {
            return;
        }
        setOrderId(paymentCancelSurveyOrderId);
        setShowCancelModal(true);
        useAssetStore.getState().clearPaymentCancelSurvey();
    }, [paymentCancelSurveyOrderId]);

    useEffect(() => {
        if (hasProcessedReturn) {
            return;
        }

        if (tryRestoreStrippedPayReturnUrl()) {
            return;
        }

        const returnSearchParams = getReturnSearchParams();
        const ret = parseHostedCheckoutReturnFromSearchParams(returnSearchParams);
        if (!ret) {
            const pending = isBackForwardNavigation() ? readPayPendingReturnUrls() : null;
            if (pending) {
                hasProcessedReturn = true;
                const meta = readPersistedPayPendingMeta(pending.orderId);
                reportCheckoutReturnFail(pending.orderId, meta, '', 'paypal_return_cancel');
                setOrderId(pending.orderId);
                if (meta) {
                    trackRechargePageResultPayCancel({
                        result_status: 'slip_cancel',
                        staticContext: metaToStaticContext(meta),
                        payExtras: metaToPayExtras(meta),
                        payment_method: meta.payment_method,
                    });
                }
                addToast('Payment canceled.');
                setShowCancelModal(true);
                if (pending.orderId) {
                    clearPersistedPayPendingMeta(pending.orderId);
                }
                clearPayPendingReturnUrls();
            }
            return;
        }
        const returnScene = returnSearchParams.get(EHostedCheckoutReturnQueryKey.ReturnScene)?.trim() ?? '';
        hasProcessedReturn = true;

        try {
            // 去掉网关回跳参数后，用跳转前快照把落地五参补回地址栏
            const cleanUrl = mergeLandingPersistAfterCheckoutReturn(
                stripHostedCheckoutReturnFromUrl(window.location.href)
            );
            if (cleanUrl !== window.location.pathname + window.location.search + window.location.hash) {
                window.history.replaceState(window.history.state, '', cleanUrl);
            }
        } catch {
            // history.replaceState 在极端环境可能抛错，sessionStorage 防重仍可兜底
        }

        const meta = readPersistedPayPendingMeta(ret.orderId);
        const store = resolveConfirmStore(meta);
        setOrderId(ret.orderId);

        if (ret.status === 'cancel') {
            reportCheckoutReturnFail(ret.orderId, meta, returnScene, 'paypal_return_cancel');
            if (meta) {
                trackRechargePageResultPayCancel({
                    result_status: 'click_cancel',
                    staticContext: metaToStaticContext(meta),
                    payExtras: metaToPayExtras(meta),
                    payment_method: meta.payment_method,
                });
            }
            addToast('Payment canceled.');
            setShowCancelModal(true);
            if (ret.orderId) {
                clearPersistedPayPendingMeta(ret.orderId);
            }
            clearPayPendingReturnUrls();
            return;
        }

        if (isOrderReturnConsumed(ret.orderId)) {
            return;
        }
        markOrderReturnConsumed(ret.orderId);

        setIsConfirming(true);
        (async () => {
            try {
                const data = await confirmCommerceOrderWithPolling(
                    async args => {
                        const res = await CommerceApi.postOrderConfirmV1({
                            order_id: args.commerceOrderId,
                            client_result: {
                                status: ret.status,
                                store,
                                store_order_id: args.paypalOrderId,
                            },
                            ts: String(Date.now()),
                        });
                        return assertConfirmOrderResponse(res);
                    },
                    { commerceOrderId: ret.orderId, paypalOrderId: ret.storeOrderId }
                );

                const staticContext = meta
                    ? metaToStaticContext(meta)
                    : {
                          trigger_scene: '',
                          gear_id: '',
                          is_balance_enough: 0,
                          paywall_template_id: '',
                          version: 0,
                          checksum: '',
                          book_id: '',
                          chapter_id: '',
                          chapter_price: '',
                          is_auto_unlock_on: '',
                      };
                const payExtras = meta ? metaToPayExtras(meta) : { money: 0, goods_type: 'recharge' as const };
                const paymentMethod = meta?.payment_method ?? '';

                if (data.status === 'PAID') {
                    reportCheckoutReturnSuccess(ret.orderId, meta, returnScene);
                    queuePayResultTrackingAfterRefresh({ staticContext, payExtras, payment_method: paymentMethod });
                    useAssetStore.getState().markPaymentSuccess();
                } else {
                    reportCheckoutReturnFail(
                        ret.orderId,
                        meta,
                        returnScene,
                        mapConfirmStatusToCheckoutFailReason(data.status, meta),
                        formatConfirmOrderFailDetail(data)
                    );
                    trackRechargePageResultPayFail({ staticContext, payExtras, payment_method: paymentMethod });
                    addToast('Payment not confirmed');
                }
            } catch (err) {
                reportCheckoutReturnFail(
                    ret.orderId,
                    meta,
                    returnScene,
                    'order_confirm_api_failed',
                    readArmsFailDetail(err)
                );
                if (meta) {
                    trackRechargePageResultPayFail({
                        staticContext: metaToStaticContext(meta),
                        payExtras: metaToPayExtras(meta),
                        payment_method: meta.payment_method,
                    });
                }
                addToast('Order confirmation failed');
            } finally {
                clearPersistedPayPendingMeta(ret.orderId);
                clearPayPendingReturnUrls();
                setIsConfirming(false);
            }
        })().catch(() => {
            setIsConfirming(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 只处理一次回跳
    }, []);

    return (
        <>
            <LoadingAnimation
                mask
                textClassName='!text-[12px]'
                cardClassName='w-[165px]'
                visible={isConfirming}
                text={t('pay_paying')}
            />
            <PayCancelReasonModal visible={showCancelModal} onClose={handleCancelModalClose} orderId={orderId} />
        </>
    );
}
